/**
 * SlideMockPreview.tsx
 *
 * A small 16:9 HTML/CSS structural preview for a single slide in the
 * pseudo-PPTX editor (Build Bar — Step 2: Slides).
 *
 * Does NOT render real slide content — instead it shows placeholder
 * shapes that communicate WHERE the main content areas sit for a given
 * component type. This gives the user a quick spatial orientation
 * without requiring actual image or text data.
 *
 * Visual anatomy (all items are absolutely-positioned inside a 16:9 wrapper):
 *  - Background: plantilla preview image (if provided) or a neutral gradient.
 *  - Placeholder shapes: semi-transparent rects/circles per layout.
 *  - Component-type badge: top-left, small dark chip.
 *  - Section label:       top-right, muted text.
 *  - Slide title:         bottom area, truncated to 1 line.
 *  - Slide number:        bottom-left corner.
 *
 * Selected state: blue border (`var(--color-primary)`).
 */

import { getComponentLabel } from '@/constants/slideComponents';
import styles from './SlideMockPreview.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlideMockPreviewProps {
  /** Value of `SlideConfig.component` — determines the placeholder layout. */
  component: string;
  /** If provided, used as the CSS `background-image` of the slide canvas. */
  plantillaBgUrl?: string;
  /** Slide title text (truncated to 1 line in the overlay). */
  title?: string;
  /** Section name shown in the top-right label. */
  section?: string;
  /** When true, a blue border (`var(--color-primary)`) is applied. */
  isSelected?: boolean;
  /** 1-based slide position shown in the bottom-left corner. */
  slideNumber?: number;
  /** Click handler passed to the outer div. */
  onClick?: () => void;
}

// ─── Layout placeholder renderers ────────────────────────────────────────────

/**
 * Returns JSX placeholder shapes for a given component type.
 * Each variant uses absolute-positioned divs with semantic class names
 * (`shape`, `shapeImage`, `shapeCircle`, etc.) defined in the CSS module.
 *
 * Values are expressed as CSS `%` strings so the preview scales correctly
 * at any rendered size.
 */
function renderPlaceholders(component: string, s: Record<string, string>): React.ReactNode {
  switch (component) {
    // ── Hero: Cover — centered large title rect + subtitle rect below ──────
    case 'hero_cover':
      return (
        <>
          <div className={s.shape} style={{ left: '20%', top: '30%', width: '60%', height: '18%' }} />
          <div className={s.shape} style={{ left: '30%', top: '54%', width: '40%', height: '8%' }} />
        </>
      );

    // ── Hero: Stats — 4 evenly-spaced stat boxes ───────────────────────────
    case 'hero_stats':
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={s.shape}
              style={{
                left: `${10 + i * 22}%`,
                top: '28%',
                width: '18%',
                height: '42%',
              }}
            />
          ))}
        </>
      );

    // ── Hero: Section divider — centered title only ────────────────────────
    case 'hero_section':
    case 'hero_closing':
      return (
        <div className={s.shape} style={{ left: '25%', top: '38%', width: '50%', height: '14%' }} />
      );

    // ── Hero: Client logos — horizontal row of small squares ──────────────
    case 'hero_client_logos':
      return (
        <>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={s.shapeImage}
              style={{
                left: `${8 + i * 18}%`,
                top: '35%',
                width: '14%',
                height: '28%',
              }}
            />
          ))}
        </>
      );

    // ── Content: Full-width text concept ──────────────────────────────────
    case 'concepto_full':
      return (
        <>
          <div className={s.shape} style={{ left: '8%', top: '22%', width: '55%', height: '12%' }} />
          <div className={s.shape} style={{ left: '8%', top: '40%', width: '50%', height: '7%' }} />
          <div className={s.shape} style={{ left: '8%', top: '52%', width: '45%', height: '7%' }} />
          <div className={s.shape} style={{ left: '8%', top: '64%', width: '40%', height: '7%' }} />
        </>
      );

    // ── Content: text left + image right ──────────────────────────────────
    case 'content_image_right':
    case 'product_showcase_right':
    case 'showcase_image_right_round':
      return (
        <>
          {/* Text block left */}
          <div className={s.shape} style={{ left: '6%', top: '22%', width: '40%', height: '10%' }} />
          <div className={s.shape} style={{ left: '6%', top: '38%', width: '36%', height: '7%' }} />
          <div className={s.shape} style={{ left: '6%', top: '50%', width: '32%', height: '7%' }} />
          {/* Image right */}
          <div
            className={component === 'showcase_image_right_round' ? s.shapeCircle : s.shapeImage}
            style={{ right: '6%', top: '18%', width: '38%', height: '62%' }}
          />
        </>
      );

    // ── Content: image left + text right ──────────────────────────────────
    case 'content_image_left':
    case 'product_showcase_left':
    case 'showcase_image_left_round':
      return (
        <>
          {/* Image left */}
          <div
            className={component === 'showcase_image_left_round' ? s.shapeCircle : s.shapeImage}
            style={{ left: '6%', top: '18%', width: '38%', height: '62%' }}
          />
          {/* Text block right */}
          <div className={s.shape} style={{ right: '6%', top: '22%', width: '40%', height: '10%' }} />
          <div className={s.shape} style={{ right: '6%', top: '38%', width: '36%', height: '7%' }} />
          <div className={s.shape} style={{ right: '6%', top: '50%', width: '32%', height: '7%' }} />
        </>
      );

    // ── Product: two side-by-side squares ─────────────────────────────────
    case 'product_showcase_pair':
      return (
        <>
          <div className={s.shapeImage} style={{ left: '8%', top: '18%', width: '38%', height: '62%' }} />
          <div className={s.shapeImage} style={{ right: '8%', top: '18%', width: '38%', height: '62%' }} />
        </>
      );

    // ── Product: 2-column grid ─────────────────────────────────────────────
    case 'product_grid_2':
      return (
        <>
          {[0, 1].map((i) => (
            <div
              key={i}
              className={s.shapeImage}
              style={{
                left: `${8 + i * 46}%`,
                top: '20%',
                width: '40%',
                height: '58%',
              }}
            />
          ))}
        </>
      );

    // ── Product: 3-column grid ─────────────────────────────────────────────
    case 'product_grid_3':
      return (
        <>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={s.shapeImage}
              style={{
                left: `${6 + i * 31}%`,
                top: '22%',
                width: '27%',
                height: '54%',
              }}
            />
          ))}
        </>
      );

    // ── Data: Timeline — vertical line with dots ───────────────────────────
    case 'data_timeline': {
      const DOT_POSITIONS = [22, 38, 54, 70];
      return (
        <>
          {/* Vertical line */}
          <div className={s.shapeLine} style={{ left: '28%', top: '18%', width: '2px', height: '64%' }} />
          {DOT_POSITIONS.map((top) => (
            <div key={top} className={s.shapeDot} style={{ left: '26.5%', top: `${top}%`, width: '8px', height: '8px' }} />
          ))}
          {/* Row labels */}
          {DOT_POSITIONS.map((top) => (
            <div key={`l-${top}`} className={s.shape} style={{ left: '34%', top: `${top - 2}%`, width: '32%', height: '8%' }} />
          ))}
        </>
      );
    }

    // ── Data: Cost table — grid placeholder ───────────────────────────────
    case 'data_cost_table': {
      const ROWS = [28, 40, 52, 64];
      return (
        <>
          {/* Header row */}
          <div className={s.shapeCell} style={{ left: '6%', top: '18%', width: '88%', height: '8%' }} />
          {ROWS.map((top) => (
            <div key={top} className={s.shapeCell} style={{ left: '6%', top: `${top}%`, width: '88%', height: '8%' }} />
          ))}
        </>
      );
    }

    // ── Journey overview — stacked horizontal steps ────────────────────────
    case 'journey_overview':
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={s.shape}
              style={{
                left: `${6 + i * 23}%`,
                top: '28%',
                width: '18%',
                height: '44%',
              }}
            />
          ))}
          {/* Connector lines */}
          {[0, 1, 2].map((i) => (
            <div
              key={`c-${i}`}
              className={s.shapeLine}
              style={{
                left: `${24 + i * 23}%`,
                top: '48%',
                width: '5%',
                height: '2px',
              }}
            />
          ))}
        </>
      );

    // ── Showcase: full-bleed image ─────────────────────────────────────────
    case 'showcase_full_bleed':
    case 'showcase_venue':
    case 'zona_instagrameable_showcase':
    case 'invitation_showcase':
    case 'render_showcase':
      return (
        <div className={s.shapeImage} style={{ inset: '0' }} />
      );

    // ── People: composite group ────────────────────────────────────────────
    case 'people_composite':
      return (
        <>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={s.shapeCircle}
              style={{
                left: `${20 + i * 22}%`,
                top: '20%',
                width: '18%',
                height: '32%',
              }}
            />
          ))}
          {[0, 1, 2].map((i) => (
            <div
              key={`n-${i}`}
              className={s.shape}
              style={{
                left: `${20 + i * 22}%`,
                top: '56%',
                width: '18%',
                height: '8%',
              }}
            />
          ))}
        </>
      );

    // ── People: speaker bio — avatar right + text left ─────────────────────
    case 'people_speaker_bio':
      return (
        <>
          <div className={s.shape} style={{ left: '6%', top: '22%', width: '44%', height: '10%' }} />
          <div className={s.shape} style={{ left: '6%', top: '38%', width: '38%', height: '7%' }} />
          <div className={s.shape} style={{ left: '6%', top: '50%', width: '32%', height: '7%' }} />
          <div className={s.shapeCircle} style={{ right: '8%', top: '18%', width: '28%', height: '50%' }} />
        </>
      );

    // ── Default fallback ───────────────────────────────────────────────────
    default:
      return (
        <>
          <div className={s.shape} style={{ left: '8%', top: '25%', width: '50%', height: '12%' }} />
          <div className={s.shape} style={{ left: '8%', top: '42%', width: '40%', height: '8%' }} />
          <div className={s.shapeImage} style={{ right: '6%', top: '18%', width: '34%', height: '62%' }} />
        </>
      );
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SlideMockPreview — structural layout preview for a single slide.
 *
 * Renders a 16:9 mini-canvas with placeholder shapes that convey WHERE
 * content sits for a given component type. No actual slide content is shown.
 *
 * Use at two sizes:
 *  - Sidebar thumbnail: parent constrains width (e.g., 160px).
 *  - Detail panel large preview: parent constrains width (e.g., 400px).
 */
export const SlideMockPreview = ({
  component,
  plantillaBgUrl,
  title,
  section,
  isSelected = false,
  slideNumber,
  onClick,
}: SlideMockPreviewProps) => {
  const label = getComponentLabel(component);

  return (
    <div
      className={`${styles.slide} ${isSelected ? styles.slideSelected : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-pressed={isSelected ? true : undefined}
      aria-label={title ? `Slide ${slideNumber ?? ''}: ${title}` : `Slide ${slideNumber ?? ''}`}
    >
      {/* Background layer */}
      {plantillaBgUrl && (
        <div
          className={styles.bgImage}
          style={{ backgroundImage: `url(${plantillaBgUrl})` }}
          aria-hidden
        />
      )}

      {/* Structural placeholder shapes */}
      {renderPlaceholders(component, styles)}

      {/* Component-type badge (top-left) */}
      <span className={styles.componentBadge} aria-hidden>
        {label}
      </span>

      {/* Section label (top-right) */}
      {section && (
        <span className={styles.sectionLabel} aria-hidden>
          {section}
        </span>
      )}

      {/* Slide title (bottom area) */}
      {title && (
        <span className={styles.titleLabel} aria-hidden>
          {title}
        </span>
      )}

      {/* Slide number (bottom-left corner) */}
      {slideNumber !== undefined && (
        <span className={styles.slideNumber} aria-hidden>
          {slideNumber}
        </span>
      )}
    </div>
  );
};
